import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, of, startWith } from 'rxjs';

import { MessageModule } from '@openng/optimus-ui/message';

import { GenericDropdownComponent, DropdownData, rxResourceFixed, LotType, LotTypeLabels } from '@zayan-erp/shared';
import { ElevationService, Elevation } from '@zayan-erp/home-building';

import { DsmApsService } from '../../services/dsm-aps.service';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

@Component({
  selector: 'dsm-build-spec-lot-setup',
  templateUrl: './dsm-build-spec-lot-setup.component.html',
  imports: [ReactiveFormsModule, GenericDropdownComponent, MessageModule, FloatLabelModule]
})
export class DsmBuildSpecLotSetupComponent {
  buildSpecId = input.required<string>();

  readonly #dsmApsService = inject(DsmApsService);
  readonly #elevationService = inject(ElevationService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #initialized = signal(false);

  form = new FormGroup({
    lotType: new FormControl<string | null>(null, Validators.required),
    model: new FormControl<string | null>(null),
    elevation: new FormControl<string | null>(null)
  });

  readonly lotTypeControl = this.form.get('lotType') as FormControl<string | null>;
  readonly modelControl = this.form.get('model') as FormControl<string | null>;
  readonly elevationControl = this.form.get('elevation') as FormControl<string | null>;

  readonly lotTypesDropdownData = toSignal(
    of(Object.values(LotType).map(lotType => ({ id: lotType, firstLabel: LotTypeLabels[lotType] })) as DropdownData[]),
    { initialValue: [] }
  );

  // buildSpecId is now the lot ID (from route param)
  readonly lotId = computed(() => this.buildSpecId());

  readonly selectedLotTypeId = toSignal(this.lotTypeControl.valueChanges.pipe(startWith(this.lotTypeControl.value)), {
    initialValue: null
  });

  readonly isLotTypeReg = computed(() => this.selectedLotTypeId() === LotType.Reg);

  // Get spec lot details (same API as sales: GET /Lot/spec-lots/{lotId})
  readonly specLotResource = rxResourceFixed({
    params: () => ({ lotId: this.lotId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.lotId) return of(null);
      return this.#dsmApsService.getSpecLotDetails(params.lotId);
    }
  });

  readonly specLot = computed(() => this.specLotResource.value());

  // Lot type from both user input AND the initial patch (form patches use emitEvent:false).
  readonly #lotTypeIdSignal = signal<string | null>(null);
  readonly isLotTypeSpec = computed(() => this.#lotTypeIdSignal() === LotType.Spec);

  // Spec lots: every model of the lot's project with its active elevations (same API as sales)
  readonly specModelsResource = rxResourceFixed({
    params: () => ({ projectId: this.specLot()?.projectId ?? null }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) return of([]);
      return this.#dsmApsService.getSpecLotModelElevations(params.projectId);
    }
  });

  // Other lot types: models for this lot (same API as sales: GET /Lot/{lotId}/models)
  readonly modelsResource = rxResourceFixed({
    params: () => ({ lotId: this.lotId() }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (!params.lotId) return of([]);
      return this.#dsmApsService.getModelsByLot(params.lotId);
    }
  });

  readonly modelsDropdownData = computed<DropdownData[]>(() =>
    this.isLotTypeSpec()
      ? (this.specModelsResource.value() ?? []).map(model => ({ id: model.modelId, firstLabel: model.modelName }))
      : (this.modelsResource.value() ?? [])
  );

  // Tracks the model selection from both user input AND the initial patch
  // (the form is patched with emitEvent:false, so we can't rely on valueChanges).
  readonly #selectedModelIdSignal = signal<string | null>(null);
  readonly selectedModelId = this.#selectedModelIdSignal.asReadonly();

  // Same as sales: use the project-model ID (stored in data field) for elevation call
  readonly selectedModelIdForElevations = computed(() =>
    this.isLotTypeSpec()
      ? null
      : (this.modelsDropdownData()?.find(model => model.id === this.selectedModelId())?.data ?? null)
  );

  // Get elevations (same API as sales: GET /Lot/{lotId}/model/{projectModelId}/elevations)
  readonly elevationsResource = rxResourceFixed({
    params: () => ({ lotId: this.lotId(), modelId: this.selectedModelIdForElevations() }),
    defaultValue: [] as Elevation[],
    stream: ({ params }) => {
      if (!params.lotId || !params.modelId) return of([] as Elevation[]);
      return this.#elevationService.getLotModelElevations(params.lotId, params.modelId);
    }
  });

  readonly elevationsDropdownData = computed<DropdownData[]>(() => {
    const elevations = this.isLotTypeSpec()
      ? ((this.specModelsResource.value() ?? []).find(model => model.modelId === this.selectedModelId())?.elevations ??
        [])
      : ((this.elevationsResource.value() as Elevation[] | null) ?? []);
    return elevations.map(elevation => ({
      id: elevation.id,
      firstLabel: elevation.elevationCode,
      secondLabel: elevation.description || elevation.elevationCode
    }));
  });

  // Tracks elevation selection from both user input AND the initial patch
  // (same reason as #selectedModelIdSignal: form patches use emitEvent:false).
  readonly #selectedElevationIdSignal = signal<string | null>(null);
  readonly selectedElevationId = this.#selectedElevationIdSignal.asReadonly();

  // Reactive eligibility check — same pattern as sales build-spec-overview.
  // Auto-fetches whenever lot/model/elevation change so the warning stays current
  // without blocking the auto-save flow.
  readonly eligibilityResource = rxResourceFixed<
    { isEligible: boolean; violatedRules: string[] } | null,
    { lotId: string | null; modelId: string | null; elevationId: string | null; isLotTypeReg: boolean }
  >({
    params: () => ({
      lotId: this.lotId() ?? null,
      modelId: this.selectedModelId(),
      elevationId: this.selectedElevationId(),
      isLotTypeReg: this.isLotTypeReg()
    }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.isLotTypeReg || !params.lotId || !params.modelId || !params.elevationId) {
        return of(null);
      }
      return this.#dsmApsService.checkModelElevationEligibility({
        lotId: params.lotId,
        modelId: params.modelId,
        elevationId: params.elevationId
      });
    }
  });

  readonly eligibilityResult = computed(() => this.eligibilityResource.value());
  readonly violatedRules = computed(() => this.eligibilityResult()?.violatedRules ?? []);
  readonly hasViolations = computed(
    () => !this.isLotTypeReg() && this.eligibilityResult()?.isEligible === false && this.violatedRules().length > 0
  );

  constructor() {
    this.lotTypeControl.valueChanges.subscribe(value => {
      this.#lotTypeIdSignal.set(value ?? null);
      if (value === LotType.Reg) {
        this.modelControl.setValue(null, { emitEvent: false });
        this.modelControl.disable({ emitEvent: false });
        this.modelControl.clearValidators();
        this.elevationControl.setValue(null, { emitEvent: false });
        this.elevationControl.disable({ emitEvent: false });
        this.elevationControl.clearValidators();
      } else {
        this.modelControl.enable({ emitEvent: false });
        this.elevationControl.enable({ emitEvent: false });
        this.modelControl.setValidators(Validators.required);
        this.elevationControl.setValidators(Validators.required);
      }
      this.modelControl.updateValueAndValidity({ emitEvent: false });
      this.elevationControl.updateValueAndValidity({ emitEvent: false });
    });

    this.modelControl.valueChanges.subscribe(value => {
      this.#selectedModelIdSignal.set(value ?? null);
      this.elevationControl.setValue(null, { emitEvent: false });
      this.#selectedElevationIdSignal.set(null);
    });

    this.elevationControl.valueChanges.subscribe(value => {
      this.#selectedElevationIdSignal.set(value ?? null);
    });

    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        if (this.#initialized()) this.#saveChanges();
      });

    effect(() => {
      const specLot = this.specLot();
      if (!specLot) return;

      const isReg = specLot.lotTypeId === LotType.Reg;
      if (isReg) {
        this.modelControl.clearValidators();
        this.elevationControl.clearValidators();
      } else {
        this.modelControl.setValidators(Validators.required);
        this.elevationControl.setValidators(Validators.required);
      }

      this.form.patchValue(
        {
          lotType: specLot.lotTypeId ?? null,
          model: specLot.selectedModel?.id ?? null,
          elevation: specLot.selectedElevation?.id ?? null
        },
        { emitEvent: false }
      );

      // Mirror the patched model/elevation into the signals so the elevations and
      // eligibility resources fetch on page load (form patch with emitEvent:false
      // skips valueChanges).
      this.#lotTypeIdSignal.set(specLot.lotTypeId ?? null);
      this.#selectedModelIdSignal.set(specLot.selectedModel?.id ?? null);
      this.#selectedElevationIdSignal.set(specLot.selectedElevation?.id ?? null);

      if (isReg) {
        this.modelControl.disable({ emitEvent: false });
        this.elevationControl.disable({ emitEvent: false });
      } else {
        this.modelControl.enable({ emitEvent: false });
        this.elevationControl.enable({ emitEvent: false });
      }

      this.modelControl.updateValueAndValidity({ emitEvent: false });
      this.elevationControl.updateValueAndValidity({ emitEvent: false });
      this.#initialized.set(true);
    });
  }

  #saveChanges(): void {
    const lotId = this.lotId();
    if (!lotId) return;
    if (this.form.invalid) return;

    const modelId = this.modelControl.value ?? null;
    const elevationId = this.elevationControl.value ?? null;
    const lotTypeId = this.lotTypeControl.value ?? null;

    if (modelId && elevationId) {
      this.#dsmApsService
        .checkModelElevationEligibility({ lotId, modelId, elevationId })
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => this.#doSave(lotId, modelId, elevationId, lotTypeId),
          error: () => this.#doSave(lotId, modelId, elevationId, lotTypeId)
        });
    } else {
      this.#doSave(lotId, modelId, elevationId, lotTypeId);
    }
  }

  #doSave(lotId: string, modelId: string | null, elevationId: string | null, lotTypeId: string | null): void {
    this.#dsmApsService
      .updateSpecLot(lotId, { lotId, modelId, elevationId, lotTypeId })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#dsmApsService.triggerSpecLotsRefresh();
        }
      });
  }
}
